// Snake (decision 2026-09-30): the game known from the old Nokia phones, alone or two players on the
// same board. The edges are walls. Hitting a wall, yourself or the other snake loses. Shared by the
// server (two-player games, computer) and the browser (solo practice).

export const SNAKE_W = 30;
export const SNAKE_H = 20;
/** One step every 250 ms (4 steps a second; user request 2026-09-30: half the original speed). */
export const SNAKE_TICK_MS = 250;
/** A two-player game lasts at most 10 minutes (user request 2026-09-30, was 3); then the longer snake wins. */
export const SNAKE_DUEL_MS = 600_000;
export const SNAKE_COUNTDOWN_MS = 3_000;
export const SNAKE_START_LENGTH = 4;
/** Turns can be queued, so that quick turns in a row are not lost. */
export const SNAKE_QUEUE = 2;

export type SnakeDir = 'up' | 'down' | 'left' | 'right';
export const SNAKE_DIRS: readonly SnakeDir[] = ['up', 'down', 'left', 'right'];

export interface Cell {
  x: number;
  y: number;
}

export interface Snake {
  /** Head first. */
  body: Cell[];
  dir: SnakeDir;
  queue: SnakeDir[];
  alive: boolean;
  /** Apples eaten. */
  score: number;
}

export interface SnakeState {
  snakes: Snake[];
  apples: Cell[];
  tick: number;
}

const DELTA: Record<SnakeDir, Cell> = { up: { x: 0, y: -1 }, down: { x: 0, y: 1 }, left: { x: -1, y: 0 }, right: { x: 1, y: 0 } };
const OPPOSITE: Record<SnakeDir, SnakeDir> = { up: 'down', down: 'up', left: 'right', right: 'left' };
export const same = (a: Cell, b: Cell) => a.x === b.x && a.y === b.y;
export const inside = (c: Cell) => c.x >= 0 && c.y >= 0 && c.x < SNAKE_W && c.y < SNAKE_H;
export const moved = (c: Cell, dir: SnakeDir): Cell => ({ x: c.x + DELTA[dir].x, y: c.y + DELTA[dir].y });
const key = (c: Cell) => c.y * SNAKE_W + c.x;

/** Apples on the board: 1 alone, 2 in a two-player game. */
export const applesFor = (players: number) => (players > 1 ? 2 : 1);

/** One snake (solo, in the middle) or two snakes facing each other. */
export function initialState(players: 1 | 2, rand: (n: number) => number): SnakeState {
  const y = Math.floor(SNAKE_H / 2);
  const line = (headX: number, dir: SnakeDir): Snake => {
    const step = dir === 'right' ? -1 : 1;
    return {
      body: Array.from({ length: SNAKE_START_LENGTH }, (_, i) => ({ x: headX + i * step, y })),
      dir,
      queue: [],
      alive: true,
      score: 0,
    };
  };
  const snakes = players === 1 ? [line(Math.floor(SNAKE_W / 2), 'right')] : [line(7, 'right'), line(SNAKE_W - 8, 'left')];
  const state: SnakeState = { snakes, apples: [], tick: 0 };
  while (state.apples.length < applesFor(players)) placeApple(state, rand);
  return state;
}

/** Queues a turn; reversing into yourself or repeating the same direction is ignored. */
export function queueTurn(snake: Snake, dir: SnakeDir): boolean {
  if (!snake.alive || snake.queue.length >= SNAKE_QUEUE) return false;
  const last = snake.queue[snake.queue.length - 1] ?? snake.dir;
  if (dir === last || dir === OPPOSITE[last]) return false;
  snake.queue.push(dir);
  return true;
}

function placeApple(state: SnakeState, rand: (n: number) => number): void {
  const taken = new Set<number>();
  for (const s of state.snakes) for (const c of s.body) taken.add(key(c));
  for (const a of state.apples) taken.add(key(a));
  const free = SNAKE_W * SNAKE_H - taken.size;
  if (free <= 0) return;
  let k = rand(free);
  for (let i = 0; i < SNAKE_W * SNAKE_H; i++) {
    if (taken.has(i)) continue;
    if (k-- === 0) {
      state.apples.push({ x: i % SNAKE_W, y: Math.floor(i / SNAKE_W) });
      return;
    }
  }
}

export interface StepResult {
  /** Indices of snakes that ate an apple / died on this step. */
  ate: number[];
  died: number[];
}

/**
 * Moves every living snake one step. A snake dies when its new head is outside the board, on any
 * snake's body (a tail that moves away this step is free), or on the other snake's new head.
 */
export function step(state: SnakeState, rand: (n: number) => number): StepResult {
  const living = state.snakes.map((s, i) => ({ s, i })).filter((x) => x.s.alive);
  const heads = new Map<number, Cell>();
  for (const { s, i } of living) {
    const next = s.queue.shift();
    if (next) s.dir = next;
    heads.set(i, moved(s.body[0]!, s.dir));
  }
  const eats = new Set<number>();
  for (const [i, h] of heads) if (state.apples.some((a) => same(a, h))) eats.add(i);

  // Cells that are occupied after the move: every body cell, except the tail of a snake that
  // does not grow on this step (it moves away).
  const occupied = new Set<number>();
  state.snakes.forEach((s, i) => {
    const keepTail = !s.alive || eats.has(i);
    s.body.forEach((c, n) => {
      if (n === s.body.length - 1 && !keepTail) return;
      occupied.add(key(c));
    });
  });

  const died = new Set<number>();
  for (const [i, h] of heads) {
    if (!inside(h) || occupied.has(key(h))) died.add(i);
    for (const [j, other] of heads) if (j !== i && same(h, other)) died.add(i);
  }
  // Two heads passing through each other.
  for (const [i, h] of heads) {
    for (const [j, other] of heads) {
      if (j <= i) continue;
      const a = state.snakes[i]!.body[0]!;
      const b = state.snakes[j]!.body[0]!;
      if (same(h, b) && same(other, a)) {
        died.add(i);
        died.add(j);
      }
    }
  }

  const ate: number[] = [];
  for (const [i, h] of heads) {
    const s = state.snakes[i]!;
    if (died.has(i)) {
      s.alive = false;
      continue;
    }
    s.body.unshift(h);
    if (eats.has(i)) {
      s.score++;
      ate.push(i);
      state.apples = state.apples.filter((a) => !same(a, h));
    } else {
      s.body.pop();
    }
  }
  while (state.apples.length < applesFor(state.snakes.length)) {
    const before = state.apples.length;
    placeApple(state, rand);
    if (state.apples.length === before) break; // board full
  }
  state.tick++;
  return { ate, died: [...died] };
}

/**
 * Result of a two-player game: the survivor wins; if both die on the same step or the time runs
 * out, the longer snake wins and equal lengths are a draw. `null` while the game goes on.
 */
export function duelWinner(state: SnakeState, timeUp: boolean): { over: boolean; winner: number | null } {
  const alive = state.snakes.map((s) => s.alive);
  if (alive.every(Boolean) && !timeUp) return { over: false, winner: null };
  if (alive.filter(Boolean).length === 1) return { over: true, winner: alive.indexOf(true) };
  const [a, b] = state.snakes;
  if (a!.body.length === b!.body.length) return { over: true, winner: null };
  return { over: true, winner: a!.body.length > b!.body.length ? 0 : 1 };
}
